import React from 'react'
import { Icon, Text } from '@lightspeed/design-system-react'
import { add } from 'vend-number'
import { formatCurrencyValue } from '@vend/utilities'
import { T } from '@transifex/react'
import { SaleDiscount } from '../sale-discount/SaleDiscount'
import { useAppSelector } from '../../../state/hooks'
import {
  usePosStateManager,
  useRegisterManager,
  useRetailerSettingsManager,
  useUserManager,
} from '../../../ng-compat/hooks'
import type { SaleDiscount as SaleDiscountTypes } from '../../../models/sale-discount'
import { selectCurrentSale } from '../../../state/slices/currentSale/currentSaleSlice'

import { isDiscountDisabled } from './discount-functions'
import { useDiscountInvalid } from './useDiscountInvalid'

const getSaleDiscount = (
  isTaxInclusive: boolean,
  discount: SaleDiscountTypes | null | undefined
) => {
  let discountValue: number | string

  // Check that the sale has a discount set
  if (discount && discount.price) {
    const { price, tax } = discount

    if (isTaxInclusive) {
      discountValue = add(price, tax)
    } else {
      discountValue = price
    }
  } else {
    // If no discount is present assume 0
    // Return early to avoid returning -0 which then makes the discount looks wrong
    return 0
  }

  return -discountValue
}

interface Props {
  showDiscountPopover: boolean
  removeSaleDiscount: () => void
  openDiscountPopover: () => void
  closePopover: () => void
}

export const DiscountRow: React.FC<Props> = ({
  showDiscountPopover,
  removeSaleDiscount,
  closePopover,
  openDiscountPopover,
}) => {
  const currentSale = useAppSelector(selectCurrentSale)
  const registerManager = useRegisterManager()
  const posStateManager = usePosStateManager()
  const userManager = useUserManager()
  const isTaxInclusive = registerManager.isTaxInclusive()
  const disabled = isDiscountDisabled(posStateManager, currentSale)
  const discount = getSaleDiscount(isTaxInclusive, currentSale.discount)
  const currentOutlet = registerManager.getActiveOutlet()
  const retailer = useRetailerSettingsManager().get()
  const discountInValid = useDiscountInvalid()

  return (
    <>
      <div className="sale-discount-line">
        <SaleDiscount
          openDiscountPopover={openDiscountPopover}
          closeDiscountPopover={closePopover}
          showPopover={!disabled && showDiscountPopover}
          isDiscountValid={!discountInValid}
          hasPermissionToDiscount={userManager.canApplySaleDiscount()}
          removeSaleDiscount={removeSaleDiscount}
        />
        <div className="sale-total-line-item--value sale-discount-line-right">
          <Text type="label">
            <b>
              {formatCurrencyValue(
                discount,
                retailer.culture,
                currentOutlet.currency,
                currentOutlet.currency_symbol
              )}
            </b>
          </Text>
        </div>
      </div>
      {discountInValid ? (
        <div className="vd-text--secondary vd-text-supplementary sale-discount-invalid">
          <div className="vd-flex vd-flex--align-center">
            <Icon icon="fa fa-info-circle vd-pl1 vd-pr1" />
            <T _str="Discount removed because it’s too high" />
          </div>
        </div>
      ) : null}
    </>
  )
}
